// Detailed sub-service pages under "Strategy & Commercial Transformation".
// Rendered by pages/ServiceDetailPage.jsx at
// /capabilities/strategy-commercial-transformation/<slug>

export const STRATEGY_SUBPAGES = [
  {
    slug: "growth-strategy",
    image: "/photos/sub-growth-strategy.jpg",
    imageAlt: "Indian leadership team reviewing a long-term growth roadmap on a wall chart",
    title: "Growth Strategy",
    metaDescription:
      "Growth strategy consulting in India to find where your next phase of profitable growth will come from — markets, categories, portfolio and channels.",
    heroSubhead: "Define where your next phase of profitable growth will come from.",
    heroParas: [
      "Growth gets harder when the obvious opportunities have already been captured.",
      "BLEZZINGS helps leadership teams identify where to grow, which opportunities deserve investment and what strategic choices are required to create the next phase of sustainable revenue and profit growth.",
    ],
    heroCta: "Build Your Growth Strategy",
    challengeLead: "Growth ambition is easy. Growth choices are harder.",
    challengeBlocks: [
      { type: "paras", items: ["Businesses often have more opportunities than they can realistically pursue:"] },
      {
        type: "list",
        items: [
          "New categories",
          "New geographies",
          "New customer segments",
          "New channels",
          "New products",
          "New business models",
          "Acquisitions",
          "Digital expansion",
        ],
      },
      { type: "paras", items: ["The challenge is deciding:"] },
      {
        type: "list",
        items: [
          "Which opportunities are genuinely attractive?",
          "Which fit the company’s capabilities?",
          "Which can create meaningful revenue and profit?",
          "Which will distract management?",
          "Which require capabilities the business does not yet have?",
          "Which opportunities should be pursued now — and which should wait?",
        ],
      },
    ],
    challengeClosing:
      "Without clear choices, growth strategy becomes a list of initiatives rather than a focused agenda.",
    perspectiveLead: "Growth strategy is ultimately a resource-allocation decision.",
    perspectiveBlocks: [
      { type: "paras", items: ["A good growth strategy answers three questions:"] },
      {
        type: "list",
        items: [
          "Where should we play? Which markets, customers, categories and opportunities are attractive?",
          "How can we win? What advantage gives the business a credible right to succeed?",
          "What should we prioritise? Where should management attention, capital and capabilities be concentrated?",
        ],
      },
      { type: "paras", items: ["Growth becomes more sustainable when these choices are connected to:"] },
      {
        type: "highlight",
        text: "Market attractiveness × Customer need × Competitive advantage × Economics × Right to win",
      },
    ],
    transform: [
      { name: "Growth Diagnostics", body: "Identify what is driving or constraining current growth." },
      { name: "Core Business Growth", body: "Find opportunities to increase penetration, usage, share or customer value within the existing business." },
      { name: "Growth Pools", body: "Identify markets, customers, categories and channels where disproportionate future growth may sit." },
      { name: "Geographic Expansion", body: "Prioritise countries, states, cities or clusters." },
      { name: "Category Expansion", body: "Evaluate adjacent or new categories." },
      { name: "Customer Expansion", body: "Identify new customer segments and use cases." },
      { name: "Adjacency Strategy", body: "Assess opportunities beyond the current core." },
      { name: "New Growth Engines", body: "Define new businesses, models or revenue streams." },
      { name: "Strategic Prioritisation", body: "Rank opportunities by attractiveness, feasibility and right to win." },
      { name: "Value-Creation Roadmap", body: "Translate strategic choices into sequenced initiatives." },
    ],
    approach: [
      { title: "Discover", body: "Understand market dynamics, customers, competitors, economics, capabilities and current performance." },
      { title: "Strategize", body: "Identify growth opportunities and make explicit choices about where to play and how to win." },
      { title: "Design", body: "Build the growth portfolio, strategic priorities and transformation roadmap." },
      { title: "Activate", body: "Translate priorities into portfolio, pricing, GTM, channel, sales and capability initiatives." },
      { title: "Optimize", body: "Track outcomes and reallocate resources toward the opportunities creating the most value." },
    ],
    deliver: [
      "Growth diagnostic",
      "Growth-pool analysis",
      "Market and category opportunity assessment",
      "Geographic opportunity map",
      "Customer growth opportunities",
      "Core-business growth plan",
      "Adjacency assessment",
      "New-business opportunity map",
      "Strategic options",
      "Growth scenarios",
      "Right-to-win assessment",
      "Opportunity prioritisation matrix",
      "Strategic priorities",
      "Value-creation plan",
      "Three-to-five-year growth roadmap",
      "Implementation agenda",
      "KPI framework",
    ],
    outcomesIntro: "Growth Strategy can help create:",
    outcomes: [
      "Clearer investment priorities",
      "Faster identification of new growth opportunities",
      "More focused resource allocation",
      "Reduced strategic distraction",
      "Stronger revenue growth",
      "Better margin mix",
      "More disciplined expansion",
      "Greater management alignment",
      "Stronger long-term strategic direction",
    ],
    impact: [
      { metric: "5× Sales Growth", body: "Relevant leadership experience includes helping build and scale a differentiated consumer business to a ₹150Cr+ annual run-rate." },
      { metric: "First Profit in a Decade", body: "Leadership experience also includes turning around an established consumer-durables category to profitability for the first time since inception." },
    ],
    impactNote:
      "Selected outcomes were delivered by BLEZZINGS leadership in prior operating and consulting roles and are not presented as BLEZZINGS client engagements.",
    whyLead: "We connect growth strategy to the commercial system required to deliver it.",
    whyParas: [
      "Growth strategies often stop at opportunity identification.",
      "BLEZZINGS can continue into:",
    ],
    whyList: [
      "Portfolio",
      "Pricing",
      "Go-to-market",
      "Channels",
      "Sales",
      "Brand",
      "Digital",
      "Commercial operating model",
    ],
    whyListAfter: 1,
    whyAfter: [
      "That allows us to move from “Where should we grow?” to “What must change for that growth to actually happen?”",
    ],
    whoLead: "Relevant when:",
    whoBullets: [
      "Growth has slowed",
      "Core markets are maturing",
      "Management needs the next growth engine",
      "You are building a three-to-five-year strategy",
      "You are evaluating adjacencies",
      "You are considering geographic expansion",
      "Too many strategic initiatives compete for attention",
      "Revenue growth is not translating into profit",
      "You need to prioritise capital across multiple opportunities",
    ],
    whoAudience:
      "Typically relevant to CEOs, founders, business heads, strategy leaders and P&L owners.",
    faqs: [
      { q: "What is Growth Strategy?", a: "Growth Strategy determines where a business should focus to create future revenue and profit growth and what strategic choices are required to win." },
      { q: "How is Growth Strategy different from Go-To-Market?", a: "Growth Strategy identifies the opportunities worth pursuing. GTM defines how a selected opportunity will be taken to market." },
      { q: "Can Growth Strategy include new categories or geographies?", a: "Yes. Category expansion, adjacencies and geographic expansion can all be part of the engagement." },
      { q: "Can BLEZZINGS help implement the growth strategy?", a: "Yes. We can continue into portfolio, pricing, GTM, channels, sales, brand, digital and commercial transformation." },
    ],
    insights: [
      "Growth ≠ Scaling",
      "Where Will Your Next ₹100 Crore Come From?",
      "Why Most Growth Strategies Have Too Many Priorities",
      "Core Growth Before Adjacent Growth",
      "The Right to Win Matters More Than Market Size",
    ],
    related: [
      { title: "Market Opportunity & Sizing", body: "Quantify the attractiveness of potential growth pools.", to: "/capabilities/market-customer-intelligence/market-opportunity-sizing" },
      { title: "Product Portfolio Strategy", body: "Translate growth priorities into portfolio choices.", to: "/capabilities/strategy-commercial-transformation/product-portfolio-strategy" },
      { title: "Go-To-Market & Market Entry", body: "Take selected opportunities to market.", to: "/capabilities/strategy-commercial-transformation/go-to-market-market-entry" },
      { title: "Commercial Excellence & Operating Model", body: "Build the system required to sustain growth.", to: "/capabilities/strategy-commercial-transformation/commercial-excellence-operating-model" },
    ],
    ctaHeading: "Growth should be a choice — not a collection of initiatives.",
    ctaBody:
      "We can help identify the opportunities most worth pursuing and build a focused roadmap for turning them into profitable growth.",
    ctaLabel: "Build Your Growth Strategy",
  },

  {
    slug: "product-portfolio-strategy",
    image: "/photos/sub-product-portfolio-strategy.jpg",
    imageAlt: "Indian category managers reviewing a lineup of consumer products on a conference table",
    title: "Product Portfolio Strategy",
    metaDescription:
      "Product portfolio strategy consulting — decide what to grow, launch, fix, maintain or exit, and concentrate investment behind the products most likely to win.",
    heroSubhead: "Build a portfolio that concentrates growth — instead of complexity.",
    heroParas: [
      "A strong portfolio gives each category, product and SKU a clear reason to exist.",
      "BLEZZINGS helps businesses decide what to grow, launch, fix, maintain or exit — and where product and commercial investment should be concentrated.",
    ],
    heroCta: "Optimise Your Product Portfolio",
    challengeLead: "More products do not necessarily mean more growth.",
    challengeParas: ["As portfolios expand:"],
    challengeSymptoms: [
      "SKUs overlap",
      "Customers become confused",
      "Marketing budgets fragment",
      "Sales teams lose focus",
      "Inventory complexity rises",
      "Low-performing products remain too long",
      "New launches cannibalise existing products",
      "Innovation loses strategic direction",
    ],
    challengeClosing:
      "Over time, a small number of products often generate most of the revenue and profit while the rest consume disproportionate resources.",
    perspectiveLead: "Every product should have a strategic and commercial role.",
    perspectiveBlocks: [
      { type: "paras", items: ["A portfolio is not a catalogue.", "Each product should contribute by:"] },
      {
        type: "list",
        items: [
          "Driving revenue",
          "Generating profit",
          "Acquiring customers",
          "Defending a price point",
          "Opening a segment",
          "Strengthening the brand",
          "Creating repeat purchase",
          "Enabling future growth",
        ],
      },
      { type: "paras", items: ["We evaluate portfolios through:"] },
      {
        type: "highlight",
        text: "Customer relevance × Competitive strength × Economics × Strategic role",
      },
    ],
    transform: [
      { name: "Portfolio Architecture", body: "Clarify the structure of categories, sub-categories, products and variants." },
      { name: "Category Strategy", body: "Determine which categories deserve growth, defence or rationalisation." },
      { name: "Product Roles", body: "Assign clear strategic roles across the portfolio." },
      { name: "SKU Performance", body: "Assess revenue, margin, growth and strategic contribution." },
      { name: "SKU Rationalisation", body: "Identify products to grow, retain, reposition, consolidate or exit." },
      { name: "Product & Price Ladders", body: "Build logical entry, mainstream, premium and flagship structures." },
      { name: "White-Space Identification", body: "Find missing needs, segments, formats and price points." },
      { name: "Innovation Priorities", body: "Direct new-product development toward the strongest opportunities." },
      { name: "Cannibalisation Management", body: "Reduce unnecessary overlap between products." },
      { name: "Lifecycle Strategy", body: "Manage launch, growth, maturity, refresh and exit." },
    ],
    approach: [
      { title: "Discover", body: "Analyse portfolio performance, customer demand, margins, competition and category dynamics." },
      { title: "Strategize", body: "Define portfolio roles and priority categories." },
      { title: "Design", body: "Build the future portfolio architecture, price-product ladder and innovation roadmap." },
      { title: "Activate", body: "Translate portfolio decisions into pricing, GTM, channel, sales and marketing priorities." },
      { title: "Optimize", body: "Track portfolio productivity and reallocate investment over time." },
    ],
    deliver: [
      "Portfolio diagnostic",
      "SKU profitability analysis",
      "Category performance analysis",
      "Portfolio architecture",
      "Product-role framework",
      "SKU rationalisation matrix",
      "Good–Better–Best architecture",
      "Product/price ladder",
      "White-space analysis",
      "Cannibalisation assessment",
      "New-product opportunity map",
      "Innovation pipeline priorities",
      "Product lifecycle framework",
      "Portfolio investment plan",
      "Implementation roadmap",
    ],
    outcomes: [
      "Higher portfolio productivity",
      "Better revenue mix",
      "Improved gross margin",
      "Lower SKU complexity",
      "Reduced cannibalisation",
      "Stronger innovation focus",
      "More effective marketing investment",
      "Better sales focus",
      "Improved inventory efficiency",
      "Clearer customer choice",
    ],
    impact: [
      { metric: "5× Sales Growth", body: "Relevant leadership experience includes helping build and scale a differentiated consumer portfolio to ₹150Cr+ annual run-rate." },
      { metric: "First Profit in a Decade", body: "Leadership experience also includes turning around a consumer-durables category to profitability for the first time in its history." },
    ],
    impactNote:
      "Selected outcomes reflect prior leadership experience and are not presented as BLEZZINGS client engagements.",
    whyLead: "We evaluate portfolios through a commercial lens.",
    whyParas: ["Portfolio decisions affect:"],
    whyList: [
      "Pricing",
      "Brand",
      "Channels",
      "Inventory",
      "Sales productivity",
      "Marketing",
      "Margin",
    ],
    whyListAfter: 0,
    whyAfter: [
      "BLEZZINGS can connect portfolio strategy with the rest of the commercial system rather than treating it as a standalone product exercise.",
    ],
    whoLead: "Relevant when:",
    whoBullets: [
      "The portfolio has become too complex",
      "Too many SKUs contribute little",
      "Product cannibalisation is increasing",
      "New launches repeatedly fail",
      "You are entering adjacent categories",
      "You need a clearer premium/value ladder",
      "Innovation lacks focus",
      "Management needs to decide where to invest",
    ],
    whoAudience:
      "Typically relevant to CEOs, business heads, category leaders, product heads, CMOs and P&L owners.",
    faqs: [
      { q: "What is Product Portfolio Strategy?", a: "It determines which products and categories should be prioritised, invested in, launched, maintained or exited." },
      { q: "How do you decide which SKUs to discontinue?", a: "We evaluate revenue, margin, growth, strategic role, customer demand and cannibalisation — not sales volume alone." },
      { q: "Can portfolio strategy include new-product development?", a: "Yes. White spaces and innovation priorities can form part of the engagement." },
      { q: "Can BLEZZINGS help implement the portfolio strategy?", a: "Yes. We can connect portfolio decisions to pricing, GTM, channels, sales and marketing." },
    ],
    insights: [
      "More SKUs ≠ More Growth",
      "How to Decide Which Products to Grow, Fix or Kill",
      "Why Portfolio Complexity Destroys Value",
      "The Good–Better–Best Portfolio Strategy",
    ],
    related: [
      { title: "Growth Strategy", body: "Determine which categories and opportunities deserve investment.", to: "/capabilities/strategy-commercial-transformation/growth-strategy" },
      { title: "Pricing & Revenue Management", body: "Build the right price architecture around the portfolio.", to: "/capabilities/strategy-commercial-transformation/pricing-revenue-management" },
      { title: "Go-To-Market & Market Entry", body: "Take priority products successfully to market.", to: "/capabilities/strategy-commercial-transformation/go-to-market-market-entry" },
      { title: "Market & Customer Intelligence", body: "Identify unmet needs and portfolio gaps.", to: "/capabilities/market-customer-intelligence" },
    ],
    ctaHeading: "Is your portfolio creating growth — or complexity?",
    ctaBody:
      "We can help simplify where needed, strengthen what matters and concentrate investment behind the products most likely to win.",
    ctaLabel: "Optimise Your Product Portfolio",
  },

  {
    slug: "pricing-revenue-management",
    image: "/photos/sub-pricing-revenue-management.jpg",
    imageAlt: "Indian commercial team analysing pricing and margin charts on a glass wall",
    title: "Pricing & Revenue Management",
    metaDescription:
      "Pricing strategy and revenue management consulting in India — price architecture, discount governance, trade schemes and price realisation that protect margin.",
    heroSubhead: "Capture more of the value you create.",
    heroParas: [
      "Pricing has a direct impact on revenue, margin and customer perception.",
      "BLEZZINGS helps businesses design pricing, discount, promotion and revenue-management systems that improve value capture without undermining sustainable growth.",
    ],
    heroCta: "Strengthen Your Pricing Strategy",
    challengeLead: "Revenue can grow while value quietly leaks away.",
    challengeParas: ["Common signs include:"],
    challengeSymptoms: [
      "Excessive discounting",
      "Weak price realisation",
      "Inconsistent customer pricing",
      "Frequent promotions",
      "Poor trade-scheme ROI",
      "Channel price conflict",
      "Weak price-pack logic",
      "Sales teams negotiating away margin",
      "Difficulty implementing price increases",
      "Competitors determining your pricing decisions",
    ],
    challengeClosing:
      "Pricing leakage often accumulates transaction by transaction — and can be difficult to see until margins deteriorate.",
    perspectiveLead: "Pricing is a commercial strategy — not a cost-plus calculation.",
    perspectiveBlocks: [
      { type: "paras", items: ["Customers pay according to:"] },
      {
        type: "list",
        items: ["Value", "Alternatives", "Differentiation", "Risk", "Convenience", "Willingness to pay"],
      },
      { type: "paras", items: ["We therefore connect:"] },
      {
        type: "highlight",
        text: "Customer Value × Competitive Position × Demand × Channel Economics × Margin",
      },
      {
        type: "paras",
        items: ["The objective is not simply a higher price.", "It is better value capture."],
      },
    ],
    transform: [
      { name: "Pricing Strategy", body: "Define the overall approach to value capture." },
      { name: "Price Architecture", body: "Create coherent pricing across products, tiers and segments." },
      { name: "Value-Based Pricing", body: "Align price more closely with perceived customer value." },
      { name: "Price-Pack Architecture", body: "Design pack and price combinations around customer needs." },
      { name: "Channel Pricing", body: "Align consumer prices, partner margins and channel realities." },
      { name: "Price Realisation", body: "Reduce leakage between list price and realised price." },
      { name: "Discount Governance", body: "Create clear rules and approval structures." },
      { name: "Promotions", body: "Design promotions around specific customer behaviours." },
      { name: "Trade Investment", body: "Improve distributor/retailer schemes and commercial spend." },
      { name: "Price Increases", body: "Plan price corrections with greater confidence." },
      { name: "Revenue Management", body: "Optimise price, mix, promotions and customer economics together." },
    ],
    approach: [
      { title: "Discover", body: "Analyse pricing, discounts, margins, customer value, competitors and channel economics." },
      { title: "Strategize", body: "Define pricing objectives and identify major value-leakage opportunities." },
      { title: "Design", body: "Build pricing architecture, discount rules, promotional systems and channel economics." },
      { title: "Activate", body: "Support rollout across customers, sales teams and channel partners." },
      { title: "Optimize", body: "Track price realisation, elasticity, mix and margin impact." },
    ],
    deliver: [
      "Pricing diagnostic",
      "Competitive price benchmark",
      "Customer value assessment",
      "Willingness-to-pay research",
      "Pricing architecture",
      "Price-pack framework",
      "Channel margin model",
      "Discount governance",
      "Trade-scheme design",
      "Promotion framework",
      "Promotional ROI analysis",
      "Price-increase plan",
      "Price realisation dashboard",
      "Revenue-management scorecard",
      "Implementation roadmap",
    ],
    outcomes: [
      "Higher price realisation",
      "Improved gross margin",
      "Better promotional ROI",
      "Lower discount leakage",
      "Stronger price architecture",
      "Better channel economics",
      "Improved revenue mix",
      "More disciplined trade spend",
      "Greater pricing confidence",
      "Higher contribution margin",
    ],
    impact: [
      { metric: "9-Point EBITDA Improvement", body: "Relevant leadership experience includes improving an e-commerce business from -6% to +3% EBITDA while simultaneously increasing sales by 66%." },
    ],
    impactNote:
      "Selected outcome delivered in a prior operating role and not represented as a BLEZZINGS client engagement.",
    whyLead: "We treat pricing as a P&L lever.",
    whyParas: ["Pricing decisions affect:"],
    whyList: [
      "Product mix",
      "Sales behaviour",
      "Channel margins",
      "Brand perception",
      "Demand",
      "Profitability",
    ],
    whyListAfter: 0,
    whyAfter: [
      "BLEZZINGS can evaluate these trade-offs together.",
      "The question is not simply: “What price should we charge?” It is: “What pricing model creates the best combination of demand, value capture and profitability?”",
    ],
    whoLead: "Relevant when:",
    whoBullets: [
      "Margins are falling",
      "Discounting has become excessive",
      "Price wars are intensifying",
      "Online and offline prices conflict",
      "You are launching a new product",
      "Trade schemes are expensive",
      "Sales teams rely too heavily on discounts",
      "You need to implement a price increase",
      "Revenue is growing faster than profit",
    ],
    whoAudience:
      "Typically relevant to CEOs, CFOs, business heads, commercial leaders, sales heads and category leaders.",
    faqs: [
      { q: "What is Revenue Management?", a: "Revenue Management optimises price, mix, promotions, discounts and customer/channel economics to improve revenue and profit." },
      { q: "Is Pricing Strategy only about increasing prices?", a: "No. It is about creating the right price architecture and improving value capture." },
      { q: "Can BLEZZINGS help with promotions?", a: "Yes. Promotions can be redesigned around specific customer behaviour and ROI." },
      { q: "Can you support B2B pricing?", a: "Yes. B2B engagements can include customer-level pricing, discount governance and negotiation structures." },
    ],
    insights: [
      "Discount ≠ Deal",
      "Why Revenue Growth Can Hide a Pricing Problem",
      "Price Realisation Matters More Than List Price",
      "The Hidden Cost of Permanent Promotions",
    ],
    related: [
      { title: "Product Portfolio Strategy", body: "Build a stronger product-price architecture.", to: "/capabilities/strategy-commercial-transformation/product-portfolio-strategy" },
      { title: "Channel & Route-to-Market", body: "Align pricing with partner economics.", to: "/capabilities/strategy-commercial-transformation/channel-route-to-market" },
      { title: "Sales Transformation", body: "Strengthen pricing discipline in the sales organisation.", to: "/capabilities/strategy-commercial-transformation/sales-transformation" },
      { title: "Customer & Buyer Insights", body: "Understand customer value and willingness to pay.", to: "/capabilities/market-customer-intelligence/customer-buyer-insights" },
    ],
    ctaHeading: "Your pricing should create profit — not just transactions.",
    ctaBody:
      "We can help identify where value is leaking and build a stronger pricing and revenue-management system.",
    ctaLabel: "Strengthen Your Pricing Strategy",
  },

  {
    slug: "go-to-market-market-entry",
    image: "/photos/sub-go-to-market-market-entry.jpg",
    imageAlt: "Indian team planning a product launch with market maps and a launch board",
    title: "Go-To-Market & Market Entry",
    metaDescription:
      "Go-to-market and market entry consulting — build the complete commercial system to launch products, enter new markets and convert opportunity into revenue.",
    heroSubhead: "Turn market opportunity into revenue.",
    heroParas: [
      "A great product does not guarantee a great launch.",
      "BLEZZINGS helps businesses build the complete commercial system required to enter new markets, launch products, reach priority customers and create revenue faster.",
    ],
    heroCta: "Build Your Go-To-Market Strategy",
    challengeLead: "Most launch failures are system failures.",
    challengeParas: ["Businesses frequently launch before answering:"],
    challengeSymptoms: [
      "Who exactly are we trying to win?",
      "Why should they choose us?",
      "What should we offer?",
      "What price should we charge?",
      "Which channels should we use?",
      "What sales motion is required?",
      "How will demand be created?",
      "What capabilities must exist before launch?",
    ],
    challengeClosing:
      "When these choices are made independently, launches become fragmented and expensive.",
    perspectiveLead: "GTM is not a marketing plan. It is the commercial system behind growth.",
    perspectiveParas: ["We connect:"],
    perspectiveHighlight:
      "Market → Customer → Proposition → Product → Price → Channel → Sales → Demand → Service → Measurement",
    perspectiveAfter: [
      "Every decision influences the others.",
      "The objective is to create a coherent market-entry system, not a collection of launch activities.",
    ],
    transform: [
      { name: "Market Entry", body: "Build the strategy for entering new countries, geographies or categories." },
      { name: "Customer Prioritisation", body: "Define target segments and Ideal Customer Profiles." },
      { name: "Proposition", body: "Clarify why target customers should choose the business." },
      { name: "Product/Offer Decisions", body: "Determine what should be launched and how." },
      { name: "Pricing", body: "Define the commercial architecture behind the launch." },
      { name: "Channel Model", body: "Determine where and how customers will buy." },
      { name: "Sales Motion", body: "Build direct, distributor, enterprise or hybrid approaches." },
      { name: "Demand Generation", body: "Define how awareness and qualified demand will be created." },
      { name: "Launch Planning", body: "Coordinate product, sales, channel, marketing and operations." },
      { name: "GTM Measurement", body: "Establish launch KPIs and feedback loops." },
    ],
    approach: [
      { title: "Discover", body: "Understand market, customer, competition, economics and organisational capabilities." },
      { title: "Strategize", body: "Define target customers, proposition, entry model and strategic choices." },
      { title: "Design", body: "Build pricing, channels, sales motion, launch architecture and operating model." },
      { title: "Activate", body: "Support launch across sales, partners and marketing." },
      { title: "Optimize", body: "Measure market response and improve the GTM model continuously." },
    ],
    deliver: [
      "Market-entry assessment",
      "Customer/ICP definition",
      "Segment prioritisation",
      "Competitive positioning",
      "Value proposition",
      "Product/offer architecture",
      "Pricing recommendation",
      "Channel model",
      "Sales motion",
      "Demand-generation plan",
      "Launch roadmap",
      "GTM operating model",
      "Roles and responsibilities",
      "KPI framework",
      "30/60/90-day activation plan",
    ],
    outcomes: [
      "Faster time-to-revenue",
      "Stronger launch performance",
      "Better product-market fit",
      "Higher channel adoption",
      "Improved sales conversion",
      "More efficient customer acquisition",
      "Reduced launch waste",
      "Faster market penetration",
      "Greater organisational alignment",
    ],
    impact: [
      { metric: "₹150Cr+ Annual Run-Rate", body: "Relevant leadership experience includes helping build and scale a differentiated consumer business to more than ₹150Cr annual run-rate." },
      { metric: "4,000+ Locations", body: "Leadership experience also includes large-scale transformation across more than 4,000 corporate and franchise locations." },
    ],
    impactNote:
      "Selected outcomes reflect prior leadership experience and are not represented as BLEZZINGS client engagements.",
    whyLead: "We can design the GTM — and connect it to execution.",
    whyParas: ["BLEZZINGS can connect:"],
    whyList: [
      "Research",
      "Strategy",
      "Portfolio",
      "Pricing",
      "Channels",
      "Sales",
      "Brand",
      "Digital",
    ],
    whyListAfter: 0,
    whyAfter: [
      "That means the GTM is built as one commercial system rather than handed from one specialist to another.",
    ],
    whoLead: "Relevant when:",
    whoBullets: [
      "Launching a new product",
      "Entering a new category",
      "Entering a new country or geography",
      "Building a new business line",
      "Moving offline to online",
      "Moving online to offline",
      "Existing launches have underperformed",
      "Sales and marketing are poorly aligned",
    ],
    whoAudience:
      "Typically relevant to CEOs, founders, business heads, CMOs, CSOs and category leaders.",
    faqs: [
      { q: "What is a Go-To-Market Strategy?", a: "It defines how a business will reach, acquire and serve customers for a specific product, market or growth opportunity." },
      { q: "How is GTM different from marketing strategy?", a: "Marketing is one part of GTM. GTM also includes customers, proposition, pricing, channels, sales and operating model." },
      { q: "Can BLEZZINGS support international market entry?", a: "Yes. We can support market assessment, partner/channel strategy, pricing, GTM and launch planning." },
      { q: "Can you support execution?", a: "Yes. Engagements can continue into sales activation, brand, digital and commercial implementation." },
    ],
    insights: [
      "Why Great Products Fail",
      "Product-Market Fit Is Not GTM Fit",
      "The 10 Decisions Every Market Entry Needs",
      "Where to Play, How to Win",
    ],
    related: [
      { title: "Market Opportunity & Sizing", body: "Validate the size and attractiveness of the opportunity.", to: "/capabilities/market-customer-intelligence/market-opportunity-sizing" },
      { title: "Product Portfolio Strategy", body: "Determine what to take to market.", to: "/capabilities/strategy-commercial-transformation/product-portfolio-strategy" },
      { title: "Pricing & Revenue Management", body: "Build the commercial architecture.", to: "/capabilities/strategy-commercial-transformation/pricing-revenue-management" },
      { title: "Channel & Route-to-Market", body: "Design how customers will be reached.", to: "/capabilities/strategy-commercial-transformation/channel-route-to-market" },
    ],
    ctaHeading: "A market opportunity has value only when you can convert it into revenue.",
    ctaBody:
      "We can help build the complete go-to-market system required to enter, launch and scale successfully.",
    ctaLabel: "Build Your Go-To-Market Strategy",
  },

  {
    slug: "channel-route-to-market",
    image: "/photos/sub-channel-route-to-market.jpg",
    imageAlt: "Indian distribution professionals reviewing a coverage map beside a stocked retail storefront",
    title: "Channel & Route-to-Market",
    metaDescription:
      "Channel and route-to-market consulting — design distribution systems that balance reach, control, customer access, partner economics and cost-to-serve.",
    heroSubhead:
      "Reach the right customers through the right channels — at the right economics.",
    heroParas: [
      "The right product means little if customers cannot access it efficiently.",
      "BLEZZINGS helps businesses design route-to-market systems that improve coverage, partner productivity, customer access and commercial economics.",
    ],
    heroCta: "Redesign Your Route-to-Market",
    challengeLead:
      "The channel model that created yesterday’s growth may constrain tomorrow’s.",
    challengeParas: ["Common symptoms include:"],
    challengeSymptoms: [
      "Poor geographic coverage",
      "Weak distributor productivity",
      "Too many channel layers",
      "High cost-to-serve",
      "Offline/online conflict",
      "Weak secondary-sales visibility",
      "Poor partner incentives",
      "Uneven territory coverage",
      "Overdependence on one channel",
      "Difficulties entering new markets",
    ],
    challengeClosing:
      "Adding more distributors is not the same as building a better route-to-market.",
    perspectiveLead: "Route-to-market is a choice between reach, control, speed and economics.",
    perspectiveParas: [
      "There is no universally superior channel model.",
      "Direct sales provides control. Distribution provides reach. E-commerce accelerates access. Partners can improve scale.",
      "The right answer is often a deliberately designed hybrid.",
      "We start with:",
    ],
    perspectiveHighlight:
      "Who is the customer? Where do they buy? What coverage is required? What route can serve them profitably?",
    perspectiveAfter: [],
    transform: [
      { name: "Route-to-Market Architecture", body: "Direct, distributor, dealer, retail, marketplace, D2C and hybrid models." },
      { name: "Distributor Strategy", body: "Define roles, partner criteria and economics." },
      { name: "Coverage Model", body: "Determine territories, outlets, accounts and service frequency." },
      { name: "Channel Economics", body: "Evaluate margins, incentives and cost-to-serve." },
      { name: "Partner Productivity", body: "Improve distributor/dealer throughput." },
      { name: "Geographic Expansion", body: "Build phased market expansion models." },
      { name: "Omnichannel Strategy", body: "Align offline, marketplace and D2C." },
      { name: "Channel Conflict", body: "Define account boundaries, pricing and governance." },
      { name: "Partner Selection", body: "Identify and evaluate distributors, dealers or other channel partners." },
    ],
    approach: [
      { title: "Discover", body: "Map current channels, customers, economics, coverage and partner performance." },
      { title: "Strategize", body: "Define priority markets, channel roles and route-to-market model." },
      { title: "Design", body: "Build partner model, margins, territories and governance." },
      { title: "Activate", body: "Support partner identification, rollout and market activation." },
      { title: "Optimize", body: "Track coverage, throughput, productivity and channel profitability." },
    ],
    deliver: [
      "Channel diagnostic",
      "Current RTM map",
      "Channel attractiveness assessment",
      "Geographic prioritisation",
      "Future RTM architecture",
      "Distributor/dealer model",
      "Partner-selection framework",
      "Channel economics",
      "Margin structure",
      "Territory design",
      "Coverage model",
      "Outlet/account segmentation",
      "Channel-conflict rules",
      "Distributor scorecard",
      "Expansion roadmap",
      "Channel KPI dashboard",
    ],
    outcomes: [
      "Greater market coverage",
      "Faster geographic expansion",
      "Higher partner productivity",
      "Better availability",
      "Improved channel profitability",
      "Reduced cost-to-serve",
      "Lower channel conflict",
      "Better secondary-sales visibility",
      "More scalable distribution",
    ],
    impact: [
      { metric: "+66% Sales Growth | 9-Point EBITDA Improvement", body: "Relevant leadership experience includes rebuilding an e-commerce channel while simultaneously improving both sales and profitability." },
      { metric: "4,000+ Locations", body: "Leadership experience also includes transformation across large corporate and franchise networks." },
    ],
    impactNote:
      "Selected outcomes reflect prior leadership experience and are not presented as BLEZZINGS client engagements.",
    whyLead: "We design channels around both reach and economics.",
    whyParas: ["BLEZZINGS evaluates:"],
    whyList: [
      "Customer access",
      "Margins",
      "Cost-to-serve",
      "Working capital",
      "Partner incentives",
      "Control",
      "Scalability",
      "Customer experience",
    ],
    whyListAfter: 0,
    whyAfter: [
      "This ensures the route-to-market creates commercial value, not just distribution width.",
    ],
    whoLead: "Relevant when:",
    whoBullets: [
      "Expanding into new states or countries",
      "Distributor productivity is weak",
      "Coverage has plateaued",
      "Online and offline channels conflict",
      "The business is overdependent on one channel",
      "Distributor margins need redesign",
      "You are evaluating direct vs indirect sales",
    ],
    whoAudience:
      "Typically relevant to CEOs, business heads, sales leaders, commercial heads and channel leaders.",
    faqs: [
      { q: "What is Route-to-Market?", a: "It is the model through which a company reaches and serves customers across direct, distributor, retail, digital or partner channels." },
      { q: "How do you choose between direct and distributor models?", a: "We assess customer density, economics, control, scalability and capability requirements." },
      { q: "Can BLEZZINGS help select distributors?", a: "Yes. We can define partner criteria, economics, scorecards and onboarding approaches." },
      { q: "Can you help resolve online/offline conflict?", a: "Yes. This may involve pricing, assortment, channel roles and governance." },
    ],
    insights: [
      "Direct vs Distributor",
      "Why More Distributors Do Not Always Mean More Sales",
      "The Economics of Omnichannel Growth",
      "How to Expand Without Overbuilding Distribution",
    ],
    related: [
      { title: "Go-To-Market & Market Entry", body: "Define the wider commercial market-entry strategy.", to: "/capabilities/strategy-commercial-transformation/go-to-market-market-entry" },
      { title: "Pricing & Revenue Management", body: "Align channel margins and partner economics.", to: "/capabilities/strategy-commercial-transformation/pricing-revenue-management" },
      { title: "Sales Transformation", body: "Align sales coverage with the channel model.", to: "/capabilities/strategy-commercial-transformation/sales-transformation" },
      { title: "Digital & Commerce", body: "Integrate marketplaces and D2C.", to: "/capabilities/digital-commerce" },
      { title: "FMCG Perspective", body: "See how distribution challenges play out in FMCG.", to: "/industries/fmcg-consumer-products" },
    ],    ctaHeading: "Is your route-to-market built for the next stage of growth?",
    ctaBody:
      "We can help improve how your business reaches customers — with better coverage, stronger partner economics and a more scalable channel model.",
    ctaLabel: "Redesign Your Route-to-Market",
  },

  {
    slug: "sales-transformation",
    image: "/photos/sub-sales-transformation.jpg",
    imageAlt: "Indian sales leadership team in a field performance review with territory boards",
    title: "Sales Transformation",
    metaDescription:
      "Sales transformation consulting — redesign sales structure, coverage, process, productivity, incentives and management discipline for scalable revenue.",
    heroSubhead: "Build a sales engine that can scale beyond individual heroics.",
    heroParas: [
      "Sustainable sales performance requires more than good salespeople.",
      "BLEZZINGS helps businesses redesign sales structure, coverage, process, productivity, incentives and management discipline to create more predictable and scalable revenue growth.",
    ],
    heroCta: "Transform Your Sales Engine",
    challengeLead:
      "When sales depends on a few individuals, growth becomes difficult to scale.",
    challengeParas: ["Common symptoms include:"],
    challengeSymptoms: [
      "Highly variable salesperson productivity",
      "Founder-dependent selling",
      "Weak pipeline visibility",
      "Poor forecasting",
      "Uneven territory potential",
      "Low lead conversion",
      "Inconsistent sales process",
      "Incentives rewarding the wrong behaviour",
      "Weak Key Account Management",
      "Managers firefighting instead of coaching",
      "Sales headcount growing faster than revenue",
    ],
    challengeClosing: "Adding more salespeople rarely fixes a poorly designed system.",
    perspectiveLead: "Sales performance is a system — not a headcount problem.",
    perspectiveParas: ["High-performing sales organisations align:"],
    perspectiveHighlight:
      "Strategy → Structure → Coverage → Process → Capability → Incentives → Management",
    perspectiveAfter: [
      "If one element is weak, performance suffers.",
      "The objective is to make sales performance repeatable rather than individual-dependent.",
    ],
    transform: [
      { name: "Sales Strategy", body: "Define the sales model aligned to customers and growth priorities." },
      { name: "Organisation Design", body: "Clarify roles, layers and responsibilities." },
      { name: "Territory & Coverage", body: "Deploy sales resources according to customer potential." },
      { name: "Customer & Account Segmentation", body: "Prioritise accounts based on value and opportunity." },
      { name: "Key Account Management", body: "Build structured approaches to strategic customers." },
      { name: "Sales Process", body: "Create consistency from prospecting through conversion." },
      { name: "Pipeline & CRM", body: "Improve opportunity visibility and follow-up." },
      { name: "Sales Productivity", body: "Reduce time spent on low-value activities." },
      { name: "Incentive Design", body: "Align rewards with strategic objectives." },
      { name: "Forecasting", body: "Improve predictability and management visibility." },
      { name: "Capability Building", body: "Strengthen selling, negotiation and account-planning skills." },
      { name: "Sales Management", body: "Build review, coaching and performance routines." },
    ],
    approach: [
      { title: "Discover", body: "Analyse performance, people, territories, pipeline, processes and incentives." },
      { title: "Strategize", body: "Define the future sales model around priority customers and growth objectives." },
      { title: "Design", body: "Build structure, territories, process, KPIs and incentives." },
      { title: "Activate", body: "Implement new tools, playbooks and management routines." },
      { title: "Optimize", body: "Track productivity, conversion and performance continuously." },
    ],
    deliver: [
      "Sales effectiveness diagnostic",
      "Sales strategy",
      "Organisation structure",
      "Role definitions",
      "Territory design",
      "Coverage model",
      "Customer segmentation",
      "Account prioritisation",
      "Key-account framework",
      "Sales playbook",
      "Pipeline stages",
      "CRM requirements",
      "Incentive structure",
      "Target-setting model",
      "Forecasting framework",
      "Sales KPI dashboard",
      "Manager review cadence",
      "Capability-development plan",
      "30/60/90-day implementation roadmap",
    ],
    outcomes: [
      "Higher sales productivity",
      "Improved conversion",
      "Greater revenue per salesperson",
      "Better account coverage",
      "Stronger pipeline visibility",
      "More reliable forecasting",
      "Better Key Account penetration",
      "Greater accountability",
      "Reduced dependency on individual performers",
      "More scalable sales growth",
    ],
    impact: [
      { metric: "5× Sales Growth", body: "Relevant leadership experience includes helping scale a differentiated consumer business fivefold." },
      { metric: "+66% Channel Sales Growth", body: "Leadership experience also includes rebuilding an e-commerce business while significantly improving profitability." },
    ],
    impactNote:
      "Selected outcomes reflect prior operating leadership experience and are not presented as BLEZZINGS client engagements.",
    whyLead: "We address the full sales system — not just structure or training.",
    whyParas: ["Sales problems may originate in:"],
    whyList: [
      "Customer segmentation",
      "Pricing",
      "Territory design",
      "Process",
      "Incentives",
      "Product proposition",
      "Channel model",
      "Management discipline",
    ],
    whyListAfter: 0,
    whyAfter: [
      "BLEZZINGS can diagnose the wider commercial context before changing the sales organisation.",
    ],
    whoLead: "Relevant when:",
    whoBullets: [
      "Revenue growth has stalled",
      "Sales headcount is growing faster than sales",
      "Territory performance varies widely",
      "Founder-led selling is difficult to scale",
      "Pipeline visibility is weak",
      "Forecasts are unreliable",
      "Incentives drive the wrong behaviour",
      "Key accounts are underdeveloped",
      "Sales and marketing are misaligned",
    ],
    whoAudience:
      "Typically relevant to CEOs, founders, CSOs, business heads and sales leaders.",
    faqs: [
      { q: "What is Sales Transformation?", a: "It is the redesign of the sales system to improve productivity, scalability and performance." },
      { q: "Is Sales Transformation the same as sales training?", a: "No. Training addresses capability. Transformation addresses the entire operating system around sales." },
      { q: "Can BLEZZINGS redesign incentives?", a: "Yes. Incentives should reinforce the behaviours and outcomes the strategy requires." },
      { q: "Do you help with CRM?", a: "We can define sales process, pipeline stages and business requirements that CRM should support." },
    ],
    insights: [
      "Sales ≠ Selling",
      "Why Adding Salespeople Does Not Automatically Increase Sales",
      "The Hidden Cost of Poor Territory Design",
      "CRM Cannot Fix a Broken Sales Process",
    ],
    related: [
      { title: "Go-To-Market & Market Entry", body: "Define the sales motion required to win a market.", to: "/capabilities/strategy-commercial-transformation/go-to-market-market-entry" },
      { title: "Channel & Route-to-Market", body: "Align direct and partner-led selling.", to: "/capabilities/strategy-commercial-transformation/channel-route-to-market" },
      { title: "Pricing & Revenue Management", body: "Give sales teams stronger pricing guardrails.", to: "/capabilities/strategy-commercial-transformation/pricing-revenue-management" },
      { title: "Commercial Excellence & Operating Model", body: "Institutionalise the management system around sales performance.", to: "/capabilities/strategy-commercial-transformation/commercial-excellence-operating-model" },
      { title: "Consumer Durables Perspective", body: "See how sales transformation applies to durables.", to: "/industries/consumer-durables-electronics-appliances" },
    ],
    ctaHeading: "Build a sales organisation that performs as a system.",
    ctaBody:
      "We can help redesign the structure, processes and management disciplines required to make sales more productive, predictable and scalable.",
    ctaLabel: "Transform Your Sales Engine",
  },

  {
    slug: "commercial-excellence-operating-model",
    image: "/photos/sub-commercial-excellence-operating-model.jpg",
    imageAlt: "Indian operations team in a management review with KPI dashboards on a large screen",
    title: "Commercial Excellence & Operating Model",
    metaDescription:
      "Commercial excellence and operating model consulting — institutionalise governance, analytics, KPIs and management discipline to make performance repeatable.",
    heroSubhead: "Make strong commercial performance repeatable.",
    heroParas: [
      "Strategy can be right. Pricing can be improved. Sales can be restructured.",
      "But without the right governance, analytics, processes, capabilities and management discipline, improvements rarely sustain.",
      "BLEZZINGS helps businesses institutionalise the commercial operating system required to turn good decisions into repeatable performance.",
    ],
    heroCta: "Build Your Commercial Operating System",
    challengeLead:
      "Commercial performance often depends too heavily on individual leaders.",
    challengeParas: [
      "Many businesses have strong people and good ideas but weak institutional systems.",
      "Common symptoms include:",
    ],
    challengeSymptoms: [
      "Decisions depend on individual judgement",
      "Sales, marketing and finance work in silos",
      "Different teams use different KPIs",
      "Commercial meetings focus on reporting rather than decisions",
      "Forecasts lack discipline",
      "Pricing exceptions proliferate",
      "Strategic initiatives lose momentum",
      "Analytics exist but do not influence action",
      "Accountability is unclear",
      "Capabilities disappear when key people leave",
      "Transformation programmes fade after initial enthusiasm",
    ],
    challengeClosing: "This makes performance difficult to scale and sustain.",
    perspectiveLead:
      "Commercial excellence is what makes good strategy survive contact with the organisation.",
    perspectiveParas: [
      "Strong commercial organisations do more than make good decisions once.",
      "They create systems that make good decisions repeatedly.",
      "That requires alignment across:",
    ],
    perspectiveHighlight:
      "Organisation → Decision Rights → Processes → Data → KPIs → Governance → Capabilities → Management Rhythm",
    perspectiveAfter: [
      "Commercial Excellence is therefore not another strategy initiative.",
      "It is the operating system that makes commercial strategy executable.",
    ],
    transform: [
      { name: "Commercial Operating Model", body: "Define how commercial functions should work together." },
      { name: "Roles & Decision Rights", body: "Clarify who owns what and where decisions sit." },
      { name: "Sales–Marketing Alignment", body: "Create shared priorities, funnels and accountability." },
      { name: "Commercial Planning", body: "Build stronger annual and periodic planning processes." },
      { name: "Performance Management", body: "Create clear KPIs and review routines." },
      { name: "Commercial Analytics", body: "Turn data into decision support." },
      { name: "Revenue Operations", body: "Improve process and data flow across marketing, sales and customer management." },
      { name: "Forecasting Discipline", body: "Strengthen revenue visibility and planning accuracy." },
      { name: "Transformation Governance", body: "Track strategic commercial initiatives systematically." },
      { name: "Capability Building", body: "Embed new skills and management practices." },
      { name: "Continuous Improvement", body: "Create mechanisms for ongoing commercial optimisation." },
    ],
    approach: [
      { title: "Discover", body: "Assess current organisation, processes, data, governance, KPIs and ways of working." },
      { title: "Strategize", body: "Define the future commercial operating model and priority capability gaps." },
      { title: "Design", body: "Build roles, decision rights, processes, dashboards and management routines." },
      { title: "Activate", body: "Implement governance, review cadences, tools and capability-building programmes." },
      { title: "Optimize", body: "Track adoption and commercial performance and refine the operating system." },
    ],
    deliver: [
      "Commercial operating-model diagnostic",
      "Future operating model",
      "Roles and responsibility matrix",
      "Decision-rights framework",
      "Sales–marketing alignment model",
      "Commercial planning process",
      "KPI architecture",
      "Commercial dashboard",
      "Revenue-operations framework",
      "Forecasting process",
      "Performance review cadence",
      "Meeting and governance architecture",
      "Commercial analytics requirements",
      "Transformation PMO/governance",
      "Capability-building roadmap",
      "Commercial playbooks",
      "Initiative tracking system",
      "Continuous-improvement framework",
    ],
    outcomesIntro: "Commercial Excellence can help create:",
    outcomes: [
      "Faster decision-making",
      "Better accountability",
      "Greater sales–marketing alignment",
      "More reliable forecasting",
      "Stronger pricing discipline",
      "Better commercial visibility",
      "Improved execution consistency",
      "Faster transformation implementation",
      "More productive management routines",
      "Reduced dependence on individual leaders",
      "More sustainable commercial performance",
    ],
    impact: [
      { metric: "4,000+ Locations", body: "Relevant leadership experience includes leading large-scale transformation across a corporate and franchise network spanning more than 4,000 locations." },
      { metric: "9-Point EBITDA Improvement", body: "Leadership experience also includes rebuilding commercial economics while simultaneously growing sales." },
    ],
    impactNote:
      "Selected outcomes reflect prior operating and consulting leadership experience and are not presented as BLEZZINGS client engagements.",
    whyLead: "We connect operating model design to real commercial outcomes.",
    whyParas: [
      "Operating models can become abstract organisation exercises.",
      "BLEZZINGS starts from the commercial results the business needs:",
    ],
    whyList: [
      "Better pricing",
      "Faster growth",
      "Higher sales productivity",
      "Stronger forecasting",
      "Better marketing effectiveness",
      "Faster GTM execution",
    ],
    whyListAfter: 1,
    whyAfter: [
      "Then we design the roles, processes, data and governance required to make those outcomes repeatable.",
    ],
    whoLead: "Relevant when:",
    whoBullets: [
      "Strategy is not translating into execution",
      "Commercial functions work in silos",
      "KPIs are unclear or inconsistent",
      "Leadership spends too much time resolving operational issues",
      "Forecasting is unreliable",
      "Transformation initiatives are losing momentum",
      "Commercial data exists but is underused",
      "Decision-making is too dependent on individuals",
      "The organisation is preparing for scale",
      "A newly redesigned sales or GTM model needs institutionalisation",
    ],
    whoAudience:
      "Typically relevant to CEOs, chief commercial officers, business heads, CSOs, CMOs, transformation leaders and P&L owners.",
    faqs: [
      { q: "What is Commercial Excellence?", a: "Commercial Excellence is the discipline of building the systems, capabilities and management practices required to make strong commercial performance repeatable." },
      { q: "How is it different from Sales Transformation?", a: "Sales Transformation focuses specifically on the sales engine. Commercial Excellence spans the broader commercial operating system across sales, marketing, pricing, analytics and governance." },
      { q: "What is a commercial operating model?", a: "It defines how commercial teams are structured, how decisions are made, how functions work together and how performance is managed." },
      { q: "Does Commercial Excellence include analytics?", a: "Yes. Commercial analytics and decision support can form an important part of the operating model." },
      { q: "Can BLEZZINGS help implement the new model?", a: "Yes. Engagements can extend into governance, capability building, playbooks, dashboards and implementation support." },
    ],
    insights: [
      "Strategy Fails in the Operating Model",
      "Commercial Excellence ≠ More Dashboards",
      "Why Good Transformations Fade After Six Months",
      "Sales–Marketing Alignment Is a Management-System Problem",
      "The Best Commercial Organisations Institutionalise Good Decisions",
    ],
    related: [
      { title: "Growth Strategy", body: "Define the priorities the commercial system must deliver.", to: "/capabilities/strategy-commercial-transformation/growth-strategy" },
      { title: "Sales Transformation", body: "Redesign the sales engine before institutionalising it.", to: "/capabilities/strategy-commercial-transformation/sales-transformation" },
      { title: "Pricing & Revenue Management", body: "Embed stronger pricing governance.", to: "/capabilities/strategy-commercial-transformation/pricing-revenue-management" },
      { title: "Digital & Commerce", body: "Connect commercial analytics with digital acquisition, conversion and retention.", to: "/capabilities/digital-commerce" },
    ],
    ctaHeading: "Build a commercial system that performs even when individual people change.",
    ctaBody:
      "We can help institutionalise the roles, processes, analytics and management disciplines required to make commercial performance more consistent and scalable.",
    ctaLabel: "Build Your Commercial Operating System",
  },
];
